import React, { useState } from "react";
import { createPortal } from "react-dom";

import useSearchPopover from "../useSearchPopover";
import getStaffInitials from "../getStaffInitials";
import "./staffSelect.scss";

const POP_W = 260;
const POP_MAXH = 320;

/**
 * Searchable staff picker — the avatar sibling of BadgeSelect. Same UX and
 * mechanics as the Weekly Task Kanban badge picker (click trigger → floating
 * searchable list that flips up/down to fit the viewport → pick one), but the
 * trigger and options render a staff avatar + name instead of a colour pill.
 *
 * Reuse anywhere we currently have a "assign staff" avatar dropdown.
 *
 * Props:
 *  - current:     selected staff object ({ Id, FirstName, LastName, ImageUrl })
 *  - list:        array of staff objects
 *  - onSelect:    (id) => void — called with the chosen staff's Id
 *  - placeholder: search box placeholder (default "Search staff…")
 *  - emptyLabel:  trigger text when nobody is selected (default "Unassigned")
 *  - sortByName:  sort options alphabetically by first+last name (default true)
 */
export default function StaffSelect({
  current,
  list,
  onSelect,
  placeholder = "Search staff…",
  emptyLabel = "Unassigned",
  sortByName = true,
  allowClear = false, // show an "Unassign" option that calls onSelect(clearValue)
  clearLabel = "Unassigned",
  clearValue = "",
}) {
  const [q, setQ] = useState("");
  const {
    open,
    setOpen,
    coords,
    anchorRef,
    popRef,
    openMenu,
    guardSelect,
    autoFocus,
  } = useSearchPopover({ width: POP_W, maxHeight: POP_MAXH });

  const handleOpen = (e) => {
    setQ("");
    openMenu(e);
  };

  const choose = guardSelect((id) => {
    onSelect?.(id);
    setOpen(false);
  });

  const stop = (e) => e.stopPropagation();

  // Support both {FirstName,LastName} and {FullName}/{Name} staff shapes.
  const rawName = (s) =>
    [s?.FirstName, s?.LastName].filter(Boolean).join(" ").trim() ||
    s?.FullName ||
    s?.Name ||
    "";

  // Never render a blank label — fall back to a Staff #id, then a dash.
  const fullName = (s) =>
    rawName(s) || (s?.Id != null ? `Staff #${s.Id}` : "");

  const initial = (s) => getStaffInitials(s) || "U";

  let options = (list || []).filter((s) =>
    fullName(s).toLowerCase().includes(q.toLowerCase())
  );
  if (sortByName) {
    options = [...options].sort((a, b) =>
      fullName(a).localeCompare(fullName(b))
    );
  }

  // Self-contained avatar — fixed-size circle whether we have a photo or not,
  // so the fallback initial never depends on theme avatar-title/avatar-xxs sizing.
  const Avatar = ({ staff }) =>
    staff?.ImageUrl ? (
      <img src={staff.ImageUrl} alt="" className="staff-select__avatar" />
    ) : (
      <span className="staff-select__avatar staff-select__avatar--initial">
        {initial(staff)}
      </span>
    );

  return (
    <>
      <span
        ref={anchorRef}
        className="staff-select__trigger"
        onClick={handleOpen}
        title="Click to change"
      >
        {current ? (
          <>
            <Avatar staff={current} />
            <span className="staff-select__name">{fullName(current)}</span>
          </>
        ) : (
          <>
            <span className="staff-select__avatar staff-select__avatar--initial staff-select__avatar--empty">
              U
            </span>
            <span className="staff-select__name text-muted">{emptyLabel}</span>
          </>
        )}
        <i className="ri-arrow-down-s-line staff-select__caret" />
      </span>

      {open &&
        coords &&
        createPortal(
          <div
            ref={popRef}
            className="staff-select__pop"
            style={{
              position: "fixed",
              left: coords.left,
              top: coords.top,
              bottom: coords.bottom,
              width: POP_W,
            }}
            // Portaled to <body>, but React still bubbles through the component
            // tree — without this, picking a staff also fires the row's onClick.
            onMouseDown={stop}
            onClick={stop}
          >
            <div className="staff-select__search">
              <i className="ri-search-line" />
              <input
                autoFocus={autoFocus}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={placeholder}
              />
            </div>
            <div
              className="staff-select__list"
              style={{ maxHeight: coords.maxHeight }}
            >
              {allowClear && !q && (
                <button
                  type="button"
                  className={`staff-select__item${
                    current ? "" : " is-active"
                  }`}
                  onClick={() => choose(clearValue)}
                >
                  <span className="staff-select__item-main">
                    <span className="staff-select__avatar staff-select__avatar--initial staff-select__avatar--empty">
                      U
                    </span>
                    <span className="staff-select__name text-muted">
                      {clearLabel}
                    </span>
                  </span>
                  {!current && (
                    <i className="ri-check-line staff-select__check" />
                  )}
                </button>
              )}
              {options.length === 0 && (
                <div className="staff-select__empty">No matches</div>
              )}
              {options.map((opt) => (
                <button
                  key={opt.Id}
                  type="button"
                  className={`staff-select__item${
                    opt.Id === current?.Id ? " is-active" : ""
                  }`}
                  onClick={() => choose(opt.Id)}
                >
                  <span className="staff-select__item-main">
                    <Avatar staff={opt} />
                    <span className="staff-select__name">{fullName(opt)}</span>
                  </span>
                  {opt.Id === current?.Id && (
                    <i className="ri-check-line staff-select__check" />
                  )}
                </button>
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
